import { useEffect, useRef, useState, useCallback } from 'react';

interface TreatmentAccordion {
  id: string;
  number: string;
  title: string;
  description: string;
  image: string;
  badge?: string;
}

const treatments: TreatmentAccordion[] = [
  {
    id: 'emagrecimento',
    number: '01',
    title: 'Emagrecimento Saudável',
    description:
      'Tratamentos personalizados para perda de peso baseada em ciência, respeitando as necessidades individuais de cada paciente.',
    image:
      'https://storage.readdy-site.link/project_files/a5718860-87a7-40cd-af11-814f205df039/5d97597b-2fc1-4fe7-9cfc-31685104c5a4_compressed_images-REAL.webp',
  },
  {
    id: 'lipedema',
    number: '02',
    title: 'Lipedema',
    description:
      'Investigação clínica completa e acompanhamento individualizado para melhorar sintomas, qualidade de vida e bem-estar.',
    image:
      'https://storage.readdy-site.link/project_files/a5718860-87a7-40cd-af11-814f205df039/1bf1ba53-c7ba-4b93-97bf-c92b90757f88_compressed_lipedema_ed61bd641d.webp',
  },
  {
    id: 'hormonal',
    number: '03',
    title: 'Reposição Hormonal',
    description:
      'Avaliação cuidadosa do equilíbrio hormonal para promover saúde, disposição e qualidade de vida.',
    image:
      'https://readdy.ai/api/search-image?query=Radiant%20mature%20woman%20in%20her%2050s%20with%20healthy%20glowing%20skin%2C%20wearing%20elegant%20cream%20silk%20blouse%2C%20soft%20natural%20window%20light%2C%20warm%20minimalist%20interior%20background%2C%20editorial%20beauty%20photography%2C%20confident%20joyful%20warm%20expression%2C%20luxury%20wellness%20magazine%20style%2C%20neutral%20beige%20tones&width=800&height=1000&seq=accordion-hormonal-v2&orientation=portrait',
  },
  {
    id: 'metabolica',
    number: '04',
    title: 'Saúde Metabólica',
    description:
      'Abordagem focada na prevenção, equilíbrio metabólico e construção de hábitos sustentáveis.',
    image:
      'https://storage.readdy-site.link/project_files/a5718860-87a7-40cd-af11-814f205df039/25d18747-3668-4a0c-84ba-c0ef51856536_compressed_saude-metabolica-1024x683.webp',
  },
  {
    id: 'estilo-vida',
    number: '05',
    title: 'Mudança de Estilo de Vida',
    description:
      'Mais do que tratar sintomas, promovemos saúde duradoura através de hábitos sustentáveis e acompanhamento contínuo.',
    image:
      'https://readdy.ai/api/search-image?query=Peaceful%20woman%20practicing%20mindfulness%20meditation%20in%20bright%20serene%20space%2C%20soft%20natural%20light%2C%20cream%20linen%20clothing%2C%20calm%20balanced%20expression%2C%20minimalist%20wellness%20interior%2C%20editorial%20lifestyle%20photography%2C%20warm%20neutral%20palette%2C%20luxury%20health%20magazine%20style&width=800&height=1000&seq=accordion-estilovida-v2&orientation=portrait',
  },
];

const additionalTreatments = [
  'Consulta de Rotina',
  'Reposição Hormonal Masculina',
  'Reposição Hormonal Feminina',
  'Protocolo Pós-Cirúrgico',
  'Check-up Metabólico',
];

function getWhatsAppLink(treatmentTitle: string): string {
  const text = `Olá! Gostaria de agendar uma consulta com a Dra. Melissa Ciríaco para tratar de ${treatmentTitle}.`;
  return `https://wa.me/5585999372109?text=${encodeURIComponent(text)}`;
}

export default function AreasAtuacao() {
  const sectionRef = useRef<HTMLElement>(null);
  const [activeIndex, setActiveIndex] = useState(4);
  const [inView, setInView] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { threshold: 0.08 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleCardHover = useCallback((index: number) => {
    setActiveIndex(index);
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.changedTouches[0].screenX;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].screenX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    const delta = touchStartX.current - touchEndX.current;
    const threshold = 50;
    if (Math.abs(delta) > threshold) {
      if (delta > 0) {
        // Swipe left → next
        setActiveIndex((prev) => Math.min(prev + 1, treatments.length - 1));
      } else {
        // Swipe right → previous
        setActiveIndex((prev) => Math.max(prev - 1, 0));
      }
    }
  }, []);

  return (
    <section
      ref={sectionRef}
      id="tratamentos"
      className="relative w-full min-h-screen py-20 md:py-28"
      style={{ backgroundColor: '#0E1412' }}
    >
      {/* Subtle gradient overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(66,85,74,0.25) 0%, transparent 70%)',
        }}
      />

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
        {/* Section header */}
        <div className="text-center mb-14 md:mb-20">
          <p
            className="text-xs tracking-[0.2em] mb-4"
            style={{
              fontFamily: 'var(--font-body)',
              color: '#C8A96A',
              fontWeight: 500,
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateY(0)' : 'translateY(12px)',
              transition: 'all 0.7s ease-out 0ms',
            }}
          >
            ÁREAS DE ATUAÇÃO
          </p>
          <h2
            className="text-3xl md:text-4xl lg:text-[2.8rem]"
            style={{
              fontFamily: 'var(--font-heading)',
              color: '#F8F7F5',
              fontWeight: 400,
              lineHeight: 1.15,
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateY(0)' : 'translateY(16px)',
              transition: 'all 0.8s ease-out 120ms',
            }}
          >
            Medicina hormonal e metabólica
          </h2>
          <p
            className="mt-4 text-sm md:text-base"
            style={{
              fontFamily: 'var(--font-body)',
              color: 'rgba(248, 247, 245, 0.5)',
              fontWeight: 300,
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateY(0)' : 'translateY(12px)',
              transition: 'all 0.7s ease-out 240ms',
            }}
          >
            Tratamento integral — a causa, não o sintoma isolado.
          </p>
        </div>

        {/* Horizontal accordion */}
        <div
          className="flex gap-2 md:gap-3 h-[520px] md:h-[580px] lg:h-[620px]"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {treatments.map((treatment, index) => {
            const isActive = activeIndex === index;
            return (
              <div
                key={treatment.id}
                onMouseEnter={() => handleCardHover(index)}
                className="relative rounded-[20px] overflow-hidden cursor-pointer"
                style={{
                  flex: isActive ? 5 : 1,
                  transition: 'flex 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
                  opacity: inView ? 1 : 0,
                  transform: inView ? 'translateY(0)' : 'translateY(30px)',
                  transitionDelay: `${index * 100}ms`,
                  transitionDuration: '0.7s',
                  backgroundColor: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(200, 169, 106, 0.12)',
                }}
              >
                {/* Background image (visible when active) */}
                <div
                  className="absolute inset-0 transition-opacity duration-700"
                  style={{
                    opacity: isActive ? 1 : 0,
                    backgroundImage: `url(${treatment.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center top',
                  }}
                >
                  {/* Dark overlay on image */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A2420] via-[#1A2420]/70 to-[#1A2420]/30" />
                </div>

                {/* Closed state — vertical text */}
                {!isActive && (
                  <div className="absolute inset-0 flex flex-col items-center justify-between py-6 px-2">
                    {/* Number */}
                    <span
                      className="text-xs tracking-widest"
                      style={{
                        fontFamily: 'var(--font-body)',
                        color: 'rgba(200, 169, 106, 0.6)',
                        fontWeight: 500,
                      }}
                    >
                      {treatment.number}
                    </span>

                    {/* Badge (if any) */}
                    {treatment.badge && (
                      <span
                        className="text-[9px] tracking-[0.08em] leading-tight text-center"
                        style={{
                          fontFamily: 'var(--font-body)',
                          color: '#C8A96A',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          writingMode: 'horizontal-tb',
                        }}
                      >
                        {treatment.badge}
                      </span>
                    )}

                    {/* Vertical title */}
                    <span
                      className="text-sm md:text-base whitespace-nowrap"
                      style={{
                        fontFamily: 'var(--font-heading)',
                        color: 'rgba(248, 247, 245, 0.55)',
                        fontWeight: 400,
                        writingMode: 'vertical-rl',
                        textOrientation: 'mixed',
                        transform: 'rotate(180deg)',
                        letterSpacing: '0.02em',
                      }}
                    >
                      {treatment.title}
                    </span>
                  </div>
                )}

                {/* Active state — full content */}
                {isActive && (
                  <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8 lg:p-10">
                    {/* Number */}
                    <span
                      className="absolute top-5 left-5 text-xs tracking-widest"
                      style={{
                        fontFamily: 'var(--font-body)',
                        color: '#C8A96A',
                        fontWeight: 500,
                      }}
                    >
                      {treatment.number}
                    </span>

                    {/* Content */}
                    <div className="max-w-[420px]">
                      <h3
                        className="text-2xl md:text-3xl lg:text-[2rem] mb-3"
                        style={{
                          fontFamily: 'var(--font-heading)',
                          color: '#F8F7F5',
                          fontWeight: 400,
                          lineHeight: 1.15,
                        }}
                      >
                        {treatment.title}
                      </h3>
                      <p
                        className="text-sm md:text-base leading-[1.8] mb-5"
                        style={{
                          fontFamily: 'var(--font-body)',
                          color: 'rgba(248, 247, 245, 0.65)',
                          fontWeight: 300,
                          maxWidth: '360px',
                        }}
                      >
                        {treatment.description}
                      </p>
                      <a
                        href={getWhatsAppLink(treatment.title)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.08em] uppercase whitespace-nowrap cursor-pointer group"
                        style={{
                          fontFamily: 'var(--font-body)',
                          color: '#C8A96A',
                        }}
                      >
                        Ver tratamento
                        <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1" />
                      </a>
                    </div>
                  </div>
                )}

                {/* Golden accent line when active */}
                <div
                  className="absolute bottom-0 left-0 right-0 h-[2px] transition-all duration-500"
                  style={{
                    backgroundColor: isActive ? '#C8A96A' : 'transparent',
                    opacity: isActive ? 0.6 : 0,
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* Mobile swipe indicators */}
        <div className="flex md:hidden items-center justify-center gap-2 mt-5">
          {treatments.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setActiveIndex(index)}
              className="rounded-full transition-all duration-300 cursor-pointer"
              style={{
                width: activeIndex === index ? 24 : 8,
                height: 8,
                backgroundColor: activeIndex === index ? '#C8A96A' : 'rgba(200, 169, 106, 0.3)',
                border: 'none',
              }}
              aria-label={`Mostrar área ${index + 1}`}
            />
          ))}
        </div>

        {/* Also treats */}
        <div
          className="mt-14 md:mt-20 text-center"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(16px)',
            transition: 'all 0.8s ease-out 600ms',
          }}
        >
          <p
            className="text-[10px] tracking-[0.2em] mb-5"
            style={{
              fontFamily: 'var(--font-body)',
              color: 'rgba(200, 169, 106, 0.5)',
              fontWeight: 500,
              textTransform: 'uppercase',
            }}
          >
            Também tratamos
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 md:gap-x-6">
            {additionalTreatments.map((item, i) => (
              <span key={item} className="flex items-center gap-2 md:gap-3">
                <span
                  className="text-sm md:text-base"
                  style={{
                    fontFamily: 'var(--font-body)',
                    color: 'rgba(248, 247, 245, 0.4)',
                    fontWeight: 300,
                  }}
                >
                  {item}
                </span>
                {i < additionalTreatments.length - 1 && (
                  <span
                    className="text-xs"
                    style={{
                      color: 'rgba(200, 169, 106, 0.25)',
                    }}
                  >
                    •
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}